/**
 * SpiritNudgeCard — Adaptive Spirit messages (amnesty, reset, extend, next-level).
 * Fetches /api/programs/{program}/spirit-nudge and shows top-priority nudge inline.
 */

import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, Shield, RotateCcw, Rocket, Heart, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;
const authHeaders = () => ({
  "Content-Type": "application/json",
  Authorization: `Bearer ${localStorage.getItem("token")}`,
});

const CODE_META = {
  amnesty: { Icon: Shield, color: "#06b6d4" },
  reset_day: { Icon: RotateCcw, color: "#f97316" },
  extend_phase: { Icon: Heart, color: "#a855f7" },
  next_level: { Icon: Rocket, color: "#00ff88" },
};

export default function SpiritNudgeCard({ program = "keto_fusion" }) {
  const [nudges, setNudges] = useState([]);
  const [dismissed, setDismissed] = useState(new Set());

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/api/programs/${program}/spirit-nudge`, { headers: authHeaders() });
        const data = await res.json();
        setNudges(data.nudges || []);
      } catch { /* silent */ }
    })();
  }, [program]);

  const visible = nudges.filter((n) => !dismissed.has(n.code));
  if (!visible.length) return null;

  const nudge = visible[0]; // Show highest priority
  const meta = CODE_META[nudge.code] || { Icon: Sparkles, color: "#84cc16" };
  const Icon = meta.Icon;

  const handleAction = async () => {
    try {
      await fetch(`${API}/api/programs/${program}/spirit-nudge/act`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ code: nudge.code, action: nudge.action }),
      });
      toast.success("Spirit aplicou o ajuste ✓");
      setDismissed((s) => new Set([...s, nudge.code]));
    } catch {
      toast.error("Erro ao aplicar");
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mb-4 p-4 rounded-2xl border relative overflow-hidden"
      style={{ borderColor: `${meta.color}40`, background: `${meta.color}10` }}
      data-testid={`spirit-nudge-${nudge.code}`}
    >
      <div className="flex items-start gap-3">
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ duration: 3, repeat: Infinity }}
          className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
          style={{ background: `${meta.color}25`, boxShadow: `0 0 15px ${meta.color}50` }}
        >
          <Icon className="w-5 h-5" style={{ color: meta.color }} />
        </motion.div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-widest mb-1" style={{ color: meta.color }}>
            Spirit · {nudge.tone}
          </p>
          <h4 className="text-sm font-bold text-zinc-100 mb-1">{nudge.title_pt}</h4>
          <p className="text-[12px] text-zinc-300 leading-snug mb-3">{nudge.body_pt}</p>
          <div className="flex gap-2">
            <Button
              onClick={handleAction}
              className="h-8 text-xs font-bold"
              style={{ background: meta.color, color: "#000" }}
              data-testid={`nudge-cta-${nudge.code}`}
            >
              {nudge.cta_pt}
            </Button>
            <button
              onClick={() => setDismissed((s) => new Set([...s, nudge.code]))}
              className="text-zinc-500 hover:text-zinc-300 px-2"
              data-testid={`nudge-dismiss-${nudge.code}`}
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
